import { useRemoteConfig } from 'context/RemoteConfigContextProvider';
import { useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Button, SelectForm } from 'shared/Components';
import { Box, Text } from 'shared/Typhography';
import { FlexContainer } from 'shared/Typhography/flex-box';
import {
  StoryChapterFields,
  StoryChapterFieldsType,
  StoryFields,
  StoryFieldsType,
} from 'validations/story';
import { theme } from 'theme';

import { ExpectedVideoPuzzleFiles } from '../../ManageVideoPuzzleScreen/constants/ExpectedVideoPuzzleFiles';
import { HasImageOrVideo, NoFile, TextEditor } from '../components';
import { FileState } from '../types';
import { FileUpload } from '../components/FileUpload';

const dbFieldsToformFields = {
  iconAnimated: 'iconAnimatedUrl',
  icon: 'iconUrl',
  previewPlaceholder: 'previewPlaceholderUrl',
  original: 'original',
  previewVideo: 'previewVideoUrl',
  video: 'videoUrl',
} as const;

interface StoryChapterFormProps {
  index: number;
  onRemove: (index: number) => void;
  disabled: boolean;
  update: (index: number, value: Partial<StoryFieldsType['chapters'][number]>) => void;
  swapPosition?: (previusIndex: number, nextIndex: number) => void;
  chapterId?: string;
  setFile?: (key: string, file: File) => void;
  shouldHideRemoveButton?: boolean;
}

export const StoryChapterForm = ({
  index,
  onRemove,
  disabled,
  swapPosition,
  chapterId,
  setFile,
  shouldHideRemoveButton,
}: StoryChapterFormProps) => {
  const [isMinimized, setMinimized] = useState<boolean>(true);
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const { storyDifficulities } = useRemoteConfig();

  const data = getValues();

  const difficultiesOptions = useMemo(() => {
    if (!storyDifficulities) {
      return [];
    }

    return Object.keys(storyDifficulities)
      .map((difficultyShape) =>
        storyDifficulities[difficultyShape as keyof typeof storyDifficulities].map(
          (difficulty) => ({
            label: difficulty,
            value: `${difficultyShape}${difficulty}`,
          }),
        ),
      )
      .flat();
  }, [storyDifficulities]);

  const chapterData = useMemo<StoryChapterFieldsType>(() => data?.chapters?.[index], [index, data]);
  const type = useMemo(() => chapterData.type, [chapterData]);
  const chapterErrors = useMemo(() => errors.chapters?.[index], [errors.chapters?.[index]]);

  // This can not be in the hook, as it does not work on hook triggers
  const previewImage = (() => {
    if (chapterData?.type === 'video') {
      return chapterData.iconAnimatedUrl?.url ?? chapterData.iconUrl?.url;
    }

    return chapterData?.iconUrl?.url ?? chapterData?.original?.url;
  })();

  const staticFile = (
    <FileUpload
      isEditDisabled={disabled}
      initialState={chapterData.original}
      onFileAddedHook={(fileState?: FileState, file?: File) => {
        setValue(`${StoryFields.Chapters}.${index}.${StoryChapterFields.Original}`, fileState);
        setFile &&
          file &&
          setFile(`${StoryFields.Chapters}.${chapterId}.${StoryChapterFields.Original}`, file);
      }}
      hasFileRender={(fileState?: FileState, removeFile?: () => void, disabled?: boolean) => (
        <HasImageOrVideo
          fileState={fileState}
          removeImage={removeFile}
          mediaType="image"
          disabled={disabled}
        />
      )}
      noFileRender={() => <NoFile fileTypes={['jpeg', 'jpg', 'png']} />}
      fileUploadProps={{
        styling: {
          dimensions: { flex: '1', width: '100%', minHeight: '300px' },
          gap: '10px',
          justifyContent: 'center',
          alignItems: 'center',
          padding: { padding: '16px' },
        },
        accept: 'image/jpg,image/jpeg,image/png',
      }}
    />
  );

  const videoFileHookData = ExpectedVideoPuzzleFiles.map((videoPuzzleFile) => {
    const fileStateKey =
      dbFieldsToformFields[videoPuzzleFile?.name as keyof typeof dbFieldsToformFields];
    const initialState = chapterData[fileStateKey as keyof typeof chapterData] as
      | FileState
      | undefined;

    return (
      <FlexContainer
        key={`video.${videoPuzzleFile.name}.${chapterId}`}
        flexDirection="column"
        dimensions={{ minWidth: '512px', flex: 1 }}
        justifyContent="flex-start"
        gap="5px"
        style={{ display: type !== 'video' ? 'none' : undefined }}
      >
        <Text weight="bold">{videoPuzzleFile.label}</Text>
        <FileUpload
          isEditDisabled={disabled}
          initialState={initialState}
          onFileAddedHook={(fileState?: FileState, file?: File) => {
            setValue(`${StoryFields.Chapters}.${index}.${fileStateKey}`, fileState);
            setFile &&
              file &&
              setFile(`${StoryFields.Chapters}.${chapterId}.${fileStateKey}`, file);
          }}
          hasFileRender={(fileState?: FileState, removeFile?: () => void, disabled?: boolean) => (
            <HasImageOrVideo
              fileState={fileState}
              removeImage={removeFile}
              mediaType={videoPuzzleFile.accept.includes('video') ? 'video' : 'image'}
              disabled={disabled}
            />
          )}
          noFileRender={() => (
            <NoFile
              fileTypes={videoPuzzleFile.accept
                .split(',')
                .map((memeType) => memeType.split('/')[1])}
            />
          )}
          fileUploadProps={{
            styling: {
              dimensions: { flex: '1', width: '100%', minHeight: '300px' },
              gap: '10px',
              justifyContent: 'center',
              alignItems: 'center',
              padding: { padding: '16px' },
            },
            accept: videoPuzzleFile.accept,
          }}
        />
      </FlexContainer>
    );
  });

  return (
    <Box
      width="100%"
      padding="10px"
      style={{ border: `solid 1px ${chapterErrors ? theme.colors.red : 'transparent'}` }}
    >
      <FlexContainer flexDirection="column" dimensions={{ width: '100%' }} gap="5px">
        <FlexContainer
          flexDirection="row"
          gap="20px"
          alignItems="center"
          dimensions={{ width: '100%' }}
        >
          <FlexContainer flexDirection="row" gap="10px" alignItems="center">
            <Text size={1.2} weight="bold">
              Chapter {index + 1}
            </Text>
            {previewImage && (
              <FlexContainer dimensions={{ width: '128px', height: '128px' }}>
                <img
                  src={previewImage}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </FlexContainer>
            )}
            <SelectForm
              required
              placeholder="Chapter difficulty"
              name={`${StoryFields.Chapters}.${index}.${StoryChapterFields.Difficulty}`}
              control={control}
              disabled={disabled}
              values={difficultiesOptions}
              error={chapterErrors?.[StoryChapterFields.Difficulty]}
              defaultValue={chapterData?.difficulty}
              width="400px"
              style={{ position: 'relative' }}
            />
          </FlexContainer>
          {swapPosition && !disabled && (
            <FlexContainer
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              dimensions={{ flex: 1 }}
              gap="5px"
            >
              {index !== 0 && (
                <Button style={{ minWidth: 100 }} onClick={() => swapPosition(index, index - 1)}>
                  MOVE UP
                </Button>
              )}
              {index !== data.chapters.length - 1 && (
                <Button style={{ minWidth: 100 }} onClick={() => swapPosition(index, index + 1)}>
                  MOVE DOWN
                </Button>
              )}
            </FlexContainer>
          )}
          <FlexContainer
            dimensions={{ flex: 1 }}
            flexDirection="column"
            justifyContent="flex-start"
            alignItems="flex-end"
          >
            <Button style={{ minWidth: 100 }} onClick={() => setMinimized(!isMinimized)}>
              {isMinimized ? <>Open</> : <>Minimized</>}
            </Button>
          </FlexContainer>
        </FlexContainer>
        <FlexContainer
          flexDirection="column"
          dimensions={{ width: '100%', height: isMinimized ? '0px' : undefined }}
          overflow={{ overflow: 'hidden' }}
          gap="10px"
        >
          <FlexContainer
            flexDirection="row"
            gap="10px"
            dimensions={{ width: '100%' }}
            flexWrap="wrap"
          >
            <FlexContainer
              key={`static.${chapterId}`}
              flexDirection="column"
              dimensions={{ minWidth: '512px', flex: 1 }}
              justifyContent="flex-start"
              gap="5px"
              style={{ display: type !== 'static' ? 'none' : undefined }}
            >
              <Text weight="bold">Image</Text>
              {staticFile}
            </FlexContainer>
            {videoFileHookData}
          </FlexContainer>
          <FlexContainer flexDirection="column" dimensions={{ width: '100%' }} gap="5px">
            <Text weight="bold">Chapter Introduction</Text>
            <Controller
              name={`${StoryFields.Chapters}.${index}.${StoryChapterFields.ChapterIntroduction}`}
              control={control}
              render={({ field }) => (
                <TextEditor
                  value={field.value}
                  disabled={false}
                  setValue={field.onChange}
                  placeholder="Start typing..."
                />
              )}
            />
            {chapterErrors?.[StoryChapterFields.ChapterIntroduction]?.message && (
              <Text color={theme.colors.red} size={11 / 16} weight="bold">
                {chapterErrors?.[StoryChapterFields.ChapterIntroduction]?.message}
              </Text>
            )}
          </FlexContainer>
        </FlexContainer>

        <FlexContainer flexDirection="row" dimensions={{ width: '100%' }} justifyContent="flex-end">
          {shouldHideRemoveButton || (
            <Button
              style={{ minWidth: 200 }}
              variant="danger"
              disabled={false}
              height={30}
              onClick={() => onRemove(index)}
            >
              Remove
            </Button>
          )}
        </FlexContainer>
      </FlexContainer>
    </Box>
  );
};
